import { useRef, useEffect, useState } from "react";
import Explore from "./Explore/Explore";
import SearchSuggestionList from "../Components/SearchSuggestionList";
import { useSearchEngine } from "./useSearch";
import { Helmet } from "react-helmet";

const discoveryTexts = [
  {
    title: "Find your next vibe",
    subtitle: "Discover places that match the mood you’re in.",
    placeholder: "Chill spots, cozy cafés, cool hangouts..."
  },
  {
    title: "Where to try next?",
    subtitle: "Explore new places you haven’t been to yet.",
    placeholder: "Rooftop bars, cute cafés, hidden corners..."
  },
  {
    title: "Your next try-spot",
    subtitle: "Find a new spot to check out with friends.",
    placeholder: "Quiet lounges, small cafés, relaxed spots..."
  },
  {
    title: "Find your next moment",
    subtitle: "Discover places that feel right for today.",
    placeholder: "Calm patios, simple bars, comfortable cafés..."
  },
  {
    title: "Find your next vibe.",
    subtitle: "Explore spots that look good and feel good.",
    placeholder: "Photo-friendly places, nice views, clean interiors..."
  },
  {
    title: "What are you exploring today?",
    subtitle: "Try something new around you.",
    placeholder: "New finds, cool areas, local gems..."
  }
];

const HomePage = ({ onHeaderSearchChange }) => {
  const discoveryIndex = useRef(Math.floor(Math.random() * discoveryTexts.length));
  const [fade, setFade] = useState(true);
  const [currentText, setCurrentText] = useState(discoveryTexts[discoveryIndex.current]);
  const {
    query,
    setQuery,
    isFocused,
    setIsFocused,
    suggestions,
    loading,
    selectedFilters,
    clearAllFilter,
    toggleFilter,
    handleWordClick,
    handleRestaurantClick,
    submitSearch,
  } = useSearchEngine();

  const heroRef = useRef(null);
  const heroSearchRef = useRef(null);

  // 🔥 Update header search visibility based on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current || !onHeaderSearchChange) return;

      const rect = heroRef.current.getBoundingClientRect();
      const showHeaderSearch = rect.bottom <= 0;

      onHeaderSearchChange(
        showHeaderSearch,
        query,
        isFocused,
        selectedFilters,
        toggleFilter
      );
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [query, isFocused, selectedFilters, toggleFilter]);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (heroSearchRef.current && !heroSearchRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };
    if (isFocused) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFocused]);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false); // start fade out

      // wait for fade-out duration (match tailwind 300ms)
      const timeout = setTimeout(() => {
        let randomIndx;
        do {
          randomIndx = Math.floor(Math.random() * discoveryTexts.length);
        } while (randomIndx === discoveryIndex.current);

        discoveryIndex.current = randomIndx;
        setCurrentText(discoveryTexts[randomIndx]);
        setFade(true);
      }, 600); 

      return () => clearTimeout(timeout);
    }, 300000);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Helmet>
        <title>Gemio – Discover & Reserve Restaurants in Nairobi</title>
        <meta name="description" content="Discover and reserve the best restaurants in Nairobi with Gemio. Explore menus, read reviews, and book a table at top dining spots." />
        <meta name="keywords" content="Gemio, explore restaurants, restaurant discovery, restaurant reservations, restaurant menus, Nairobi restaurants, dining guide, restaurant reviews, restaurant discovery app" />
        <link rel="canonical" href="https://gemio.app/" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Gemio",
              "description": "Gemio is a restaurant discovery and reservation platform for exploring the best dining spots in Nairobi and across Kenya. Discover menus, read reviews, and book tables.",
              "url": "https://gemio.app/",
              "logo": "https://gemio.app/gemio_logo.jpg",
              "sameAs": [
                "https://www.tiktok.com/@gemioapp",
                "https://www.instagram.com/gemio.app",
                "https://www.instagram.com/gemioapp/"
              ]
            })
          }}
        />
      </Helmet>
      <div className="min-h-screen">
        {/* --- HERO --- */}
        <div
          ref={heroRef}
          className="bg-[#FFF8E7] border-b border-[#FFDDA6] pt-8 pb-12"
        >
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col items-center text-center mb-8">
              <h1 className={`text-3xl md:text-5xl font-bold text-[#111827] mb-2 lg:mb-4 transition-opacity duration-600 ${fade ? 'opacity-100' : 'opacity-0'}`}>
                {currentText.title}
              </h1>
              <p className={`text-xl text-center text-[#5E503F] max-w-2xl mx-autotransition-opacity duration-600 ${fade ? 'opacity-100' : 'opacity-0'}`}>
                {currentText.subtitle}
              </p>
            </div>

            {/* --- SEARCH BAR --- */}
            <div className="max-w-2xl mx-auto" ref={heroSearchRef}>
              <div className="relative">
                <div className="relative flex items-center">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-[#5E503F]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>

                  {/* Input */}
                  <input
                    type="text"
                    placeholder={currentText.placeholder || "Search for restaurants, cuisines, or keywords..."}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    className="w-full pl-12 pr-[120px] py-4 border border-[#FFDDA6] rounded-full"
                  />

                  {/* Search Button */}
                  <button
                    type="button"
                    onClick={() => submitSearch(query)}
                    className="absolute right-2 bg-[#FFD166] hover:bg-[#E5B955] px-6 py-2 rounded-full"
                  >
                    Search
                  </button>
                </div>

                {/* --- SUGGESTION DROPDOWN --- */}
                <SearchSuggestionList
                  query={query}
                  isFocused={isFocused}
                  suggestions={suggestions}
                  loading={loading}
                  selectedFilters={selectedFilters}
                  onFilterToggle={toggleFilter}
                  onWordClick={handleWordClick}
                  onRestaurantClick={handleRestaurantClick}
                  onClearFilters={clearAllFilter}
                />
              </div>
            </div>
          </div>
        </div>

        {/* --- EXPLORE SECTIONS --- */}
        <div className="max-w-7xl mx-auto px-2 md:px-4 py-4">
          <Explore />
        </div>
      </div>
    </>
  );
};

export default HomePage;
